'use client';

import { useTranslations } from 'next-intl';
import { useInteractiveLoader } from '~/components/PartnerLoader/PartnerLoader';
import { useToast } from '~/hooks/useToasts';
import { useUpdateTrip } from '~/hooks/useUpdateTrip';
import { useTripBookingSelection } from '~/stores/tpv2/tripBookingStore';
import { useTripOverviewSummary } from './useTripOverviewSummary';
type ConfirmTravellersInput = {
  adults: number;
  children: number;
  rooms: number;
  childAges: number[];
};
export const useTripTravellersUpdate = () => {
  const {
    travellerCount,
    rooms,
    overviewData
  } = useTripOverviewSummary();
  const travellerMixes = overviewData?.pax.travellerMixes ?? [];
  const adultCount = travellerMixes.reduce((sum, mix) => sum + mix.adultCount, 0) || Math.max(1, travellerCount);
  const childAges = travellerMixes.flatMap(mix_0 => mix_0.childAges);
  const childCount = childAges.length;
  const totalTravellerCount = adultCount + childCount;
  const t = useTranslations('journey');
  const toast = useToast();
  const updateTrip = useUpdateTrip();
  const {
    startLoader,
    completeProgressBar
  } = useInteractiveLoader();
  const {
    rooms: selectedRooms,
    setSelection
  } = useTripBookingSelection(travellerCount, rooms);
  const confirmTravellers = async ({
    adults,
    children,
    rooms: nextRooms,
    childAges: nextChildAges
  }: ConfirmTravellersInput): Promise<boolean> => {
    startLoader();
    try {
      const result = await updateTrip({
        adult_count: adults,
        child_count: children,
        childAges: nextChildAges,
        room_count: nextRooms
      });
      if (result.error) {
        toast?.add({
          title: result.error,
          variant: 'error',
          position: 'middle'
        }, {
          timeout: 5000
        });
        return false;
      }
      setSelection({
        travellerCount: adults + children,
        rooms: nextRooms
      });
      toast?.add({
        title: t('overview_travellers_success'),
        variant: 'success',
        position: 'middle'
      }, {
        timeout: 5000
      });
      return true;
    } finally {
      completeProgressBar();
    }
  };
  return {
    selectedTravellerCount: totalTravellerCount,
    selectedAdultCount: adultCount,
    selectedChildCount: childCount,
    selectedRooms,
    adultCount,
    childAges,
    confirmTravellers
  };
};